import { Eye, EyeOff } from 'lucide-react';
import { type InputHTMLAttributes, useId, useState } from 'react';
import { inputFrame } from './TextField';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
};

/** A password input with a show/hide toggle (spec 2026-10-06 §3.1). */
export function PasswordField({ label, hint, error, className = '', ...props }: Props) {
  const id = useId();
  const errorId = `${id}-error`;
  const [shown, setShown] = useState(false);
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="block font-display text-xs font-semibold uppercase tracking-[0.14em] text-muted"
      >
        {label}
      </label>
      <div className="relative mt-1">
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          className={`${inputFrame} pr-12`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          {...props}
        />
        <button
          type="button"
          aria-label={shown ? 'Hide' : 'Show'}
          title={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          onClick={() => setShown((s) => !s)}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-muted hover:text-text"
        >
          {shown ? (
            <EyeOff className="size-5" aria-hidden />
          ) : (
            <Eye className="size-5" aria-hidden />
          )}
        </button>
      </div>
      {hint ? <p className="mt-1 text-xs text-faint">{hint}</p> : null}
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
